import { createContext, useContext, useState, ReactNode } from "react";
import { signIn, signUp, socialSignIn, socialSignUp } from "../services/authService";
import { User, SocialUser } from "../types/user";

type AuthContextType = {
    user: User;
    isAuthenticated: boolean;
    currentScreen: "signIn" | "signUp" | "forgotPassword" | "changePassword";
    isAuthOpen: boolean;
    isLoading: boolean;
    error: string | null;
    setIsAuthenticated: (isAuthenticated: boolean) => void;
    setCurrentScreen: (currentScreen: "signIn" | "signUp" | "forgotPassword" | "changePassword") => void;
    setIsAuthOpen: (isAuthOpen: boolean) => void;
    setUser: (user: User) => void;
    signInUser: (user: User) => void;
    signUpUser: (user: User) => void;
    socialSignInUser: (user: SocialUser) => void;
    socialSignUpUser: (user: SocialUser) => void;
    logout: () => void;
    getMyLocation: () => Promise<GeolocationCoordinates>;
}

export const AuthContext = createContext<AuthContextType>({ user: {} as User, isLoading: false, error: null, isAuthenticated: false, isAuthOpen: false, currentScreen: "signIn", setIsAuthenticated: () => { }, setCurrentScreen: () => { }, setIsAuthOpen: () => { }, setUser: () => { }, signInUser: () => { }, signUpUser: () => { }, socialSignInUser: () => { }, socialSignUpUser: () => { }, logout: () => { } });

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [user, setUser] = useState<User>(() => JSON.parse(localStorage.getItem("user") || "{}") as User);
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => !!localStorage.getItem("user"));
    const [currentScreen, setCurrentScreen] = useState<"signIn" | "signUp" | "forgotPassword" | "changePassword">("signIn");
    const [isAuthOpen, setIsAuthOpen] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const getMyLocation = async () => {
        return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(position => {
            resolve(position.coords);
        },
        error => {
            reject(error);
            }
        );
        });
    }
    const signInUser = async (user: User) => {
        setIsLoading(true);
        setError(null);
        signIn(user).then(response => {
            handleAuthResponse(response.data);
        }).catch(error => {
            setError(error.response.data.message);
        }).finally(() => {
            setIsLoading(false);
        });
    }

    const signUpUser = async (user: User) => {
        setIsLoading(true);
        setError(null);
        signUp(user).then(response => {
            handleAuthResponse(response.data);
        }).catch(error => {
            setError(error.response.data.message);
        }).finally(() => {
            setIsLoading(false);
        });
    }

    const socialSignInUser = async (user: SocialUser) => {
        setIsLoading(true);
        setError(null);
        socialSignIn(user).then(response => {
            handleAuthResponse(response.data);
        }).catch(error => {
            setError(error.response.data.message);
        }).finally(() => {
            setIsLoading(false);
        });
    }

    const socialSignUpUser = async (user: SocialUser) => {
        setIsLoading(true);
        setError(null);
        socialSignUp(user).then(response => {
            handleAuthResponse(response.data);
        }).catch(error => {
            setError(error.response.data.message);
        }).finally(() => {
            setIsLoading(false);
        });
    }

    const logout = () => {
        localStorage.removeItem("user");
        setUser({} as User);
        setIsAuthenticated(false);
    }

    const handleAuthResponse = (response: User) => {
        setUser(response);
        localStorage.setItem('user', JSON.stringify(response));
        setIsAuthenticated(true);
        setIsAuthOpen(false);
    }

    return <AuthContext.Provider value={{ user, isAuthenticated, currentScreen, isAuthOpen, isLoading, error, setIsAuthenticated, setCurrentScreen, setIsAuthOpen, setUser, signInUser, signUpUser, socialSignInUser, socialSignUpUser, logout, getMyLocation }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within a AuthProvider');
    }
    return context;
}